/**
 * TransferDialog Component
 * 
 * Shared transfer dialog component used by both Accounts.tsx and Activity.tsx.
 * Supports optional features like time field, balance helpers, and edit mode.
 */

import React, { FormEvent } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import { Account, Currency } from "../../types";
import { TransactionWithDate } from "../../utils/transactionProcessing";
import { formatCurrency, formatRate, getCurrentTime } from "../../utils/transactionUtils";
import RefreshIconButton from "../RefreshIconButton";
import { calculateTransferRate } from "../../utils/accountHelpers";
import { getCurrencyDecimalPlaces } from "../../utils/currencyDefaults";

export interface TransferFormData {
  amount: string;
  date: string;
  time?: string;
  note: string;
  fromAccountId: string;
  toAccountId: string;
  transferRate: string;
  attachmentFile: File | null;
  attachmentUrl: string | null;
  attachmentName: string | null;
  attachmentType: string | null;
  feeAmount?: string;
  feeAccountId?: string;
}

interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  
  // Form data
  formData: TransferFormData;
  setFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  
  // Data
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  
  // Optional features
  showTime?: boolean;
  showBalanceHelpers?: boolean;
  showRefreshRate?: boolean;
  editingTransaction?: TransactionWithDate | null;
  
  // State
  isSubmitting?: boolean;
  uploadingAttachment?: boolean;
  rateManuallyEdited?: boolean;
  setShowTime?: (show: boolean) => void;
  setShowFee?: (show: boolean) => void;
  setRateManuallyEdited?: (edited: boolean) => void;
  onRefreshRate?: () => void;
  
  // Handlers
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment?: () => void;
  
  // Refs
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  
  // Validation
  isValid?: boolean;
  getConvertedAmount?: () => number | null;
  calculateTransferRate?: (fromCurrency: string, toCurrency: string) => number;
}

export default function TransferDialog({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  accounts,
  currencies,
  defaultCurrency,
  showTime = false,
  showBalanceHelpers = false,
  showRefreshRate = false,
  editingTransaction = null,
  isSubmitting = false,
  uploadingAttachment = false,
  rateManuallyEdited = false,
  setShowTime,
  setShowFee,
  setRateManuallyEdited,
  onRefreshRate,
  onFileChange,
  onRemoveAttachment,
  fileInputRef,
  isValid = true,
  getConvertedAmount,
  calculateTransferRate: calculateTransferRateProp,
}: TransferDialogProps): React.ReactElement {
  const fromAccount = formData.fromAccountId
    ? accounts.find((acc) => acc.id === formData.fromAccountId && !acc.isArchived)
    : null;
  const toAccount = formData.toAccountId
    ? accounts.find((acc) => acc.id === formData.toAccountId && !acc.isArchived)
    : null;

  const currenciesDiffer = fromAccount && toAccount && fromAccount.currency !== toAccount.currency;

  // Use prop calculateTransferRate if provided, otherwise use utility
  const calculateRate = (fromCurrency: string, toCurrency: string): number => {
    if (calculateTransferRateProp) {
      return calculateTransferRateProp(fromCurrency, toCurrency);
    }
    return calculateTransferRate(fromCurrency, toCurrency, defaultCurrency, currencies);
  };

  // Calculate converted amount for display
  const convertedAmount = getConvertedAmount ? getConvertedAmount() : null;

  // Calculate balance after transfer (for helper text)
  const getBalanceAfterTransfer = (
    account: Account | null,
    isFromAccount: boolean
  ): { balance: number; isNegative: boolean; feeAmount?: number } | null => {
    if (!showBalanceHelpers || !account || !formData.amount) return null;

    const transferAmount = parseFloat(formData.amount || "0");
    if (isNaN(transferAmount) || transferAmount <= 0) return null;

    let balanceBeforeTransfer = account.amount;

    // If editing, reverse the old transfer
    if (editingTransaction && editingTransaction.type === "transfer") {
      if (isFromAccount && editingTransaction.fromAccountId === account.id) {
        const oldAmount = editingTransaction.amount || 0;
        balanceBeforeTransfer = account.amount + oldAmount;
      } else if (!isFromAccount && editingTransaction.toAccountId === account.id) {
        const oldAmount = editingTransaction.amount || 0;
        let oldAmountInAccountCurrency = oldAmount;

        // Handle currency conversion for old transfer
        if (editingTransaction.transferRate) {
          const oldRate = parseFloat(String(editingTransaction.transferRate));
          if (!isNaN(oldRate) && oldRate > 0) {
            oldAmountInAccountCurrency = oldAmount * oldRate;
          }
        }

        balanceBeforeTransfer = account.amount - oldAmountInAccountCurrency;
      }
    }

    let newBalance: number;
    if (isFromAccount) {
      newBalance = balanceBeforeTransfer - transferAmount;
    } else {
      // For toAccount, convert amount if currencies differ
      let amountInAccountCurrency = transferAmount;
      if (currenciesDiffer && formData.transferRate) {
        const rate = parseFloat(formData.transferRate);
        if (!isNaN(rate) && rate > 0) {
          amountInAccountCurrency = transferAmount * rate;
        }
      }
      newBalance = balanceBeforeTransfer + amountInAccountCurrency;
    }

    // Check if fee account matches this account and subtract fee
    let feeAmount: number | undefined;
    if (formData.feeAmount && formData.feeAccountId && formData.feeAccountId === account.id) {
      feeAmount = parseFloat(formData.feeAmount);
      if (!isNaN(feeAmount) && feeAmount > 0) {
        newBalance = newBalance - feeAmount;
      }
    }

    return {
      balance: newBalance,
      isNegative: newBalance < 0,
      feeAmount: feeAmount,
    };
  };

  const fromBalance = fromAccount ? getBalanceAfterTransfer(fromAccount, true) : null;
  const toBalance = toAccount ? getBalanceAfterTransfer(toAccount, false) : null;

  // Auto-fill rate when accounts change
  React.useEffect(() => {
    if (fromAccount && toAccount && currenciesDiffer) {
      const calculatedRate = calculateRate(fromAccount.currency, toAccount.currency);
      // Only auto-populate if rate field is empty or user hasn't manually edited it
      if (!formData.transferRate || formData.transferRate === "") {
        setFormData((prev) => ({
          ...prev,
          transferRate: formatRate(calculatedRate),
        }));
      }
    } else if (fromAccount && toAccount && !currenciesDiffer) {
      // Clear rate if currencies are the same
      setFormData((prev) => ({
        ...prev,
        transferRate: "",
      }));
    }
  }, [fromAccount?.id, toAccount?.id, currenciesDiffer, calculateRate]);

  // Rate refresh logic
  const handleRefreshRate = (): void => {
    if (fromAccount && toAccount && currenciesDiffer && onRefreshRate) {
      onRefreshRate();
    }
  };

  const calculatedRate =
    fromAccount && toAccount && currenciesDiffer
      ? calculateRate(fromAccount.currency, toAccount.currency)
      : null;
  const currentRate = formData.transferRate ? parseFloat(formData.transferRate) : null;
  const rateMatchesSetting =
    calculatedRate !== null &&
    currentRate !== null &&
    !isNaN(currentRate) &&
    Math.abs(currentRate - calculatedRate) < 0.000001;
  const shouldDisableRefresh = rateMatchesSetting || !rateManuallyEdited;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", position: "relative" }}>
        <Typography variant="h6">
          {editingTransaction && editingTransaction.type === "transfer"
            ? "Edit Transfer"
            : "Transfer Between Accounts"}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={onSubmit} id="transfer-form">
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Date and Time */}
            <TextField
              required
              label="Date"
              type="date"
              value={formData.date}
              onChange={(e) => {
                setFormData({ ...formData, date: e.target.value });
              }}
              sx={{ width: "100%" }}
              InputLabelProps={{ shrink: true }}
              InputProps={
                showTime && setShowTime
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon
                            sx={{
                              color: "text.secondary",
                              fontSize: "1.25rem",
                            }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {!formData.time ? (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowTime(true);
                                setFormData({ ...formData, time: getCurrentTime() });
                              }}
                              sx={{
                                minWidth: "auto",
                                px: 1,
                                py: 0.5,
                                color: "text.primary",
                                fontWeight: "normal",
                                textTransform: "none",
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                              }}
                            >
                              Time
                            </Button>
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <TextField
                                type="time"
                                value={formData.time || ""}
                                onChange={(e) => {
                                  setFormData({ ...formData, time: e.target.value });
                                }}
                                size="small"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                sx={{
                                  width: 100,
                                  "& .MuiOutlinedInput-root": {
                                    height: 32,
                                    paddingLeft: 0,
                                    paddingRight: 0,
                                    "& fieldset": {
                                      borderColor: "rgba(0, 0, 0, 0.23) !important",
                                    },
                                  },
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setFormData({ ...formData, time: "" });
                                  setShowTime(false);
                                }}
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "text.primary",
                                    bgcolor: "action.hover",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />

            {/* Amount */}
            <TextField
              required
              label="Amount"
              type="number"
              inputProps={{ step: "any", min: "0" }}
              value={formData.amount}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow digits and a single decimal point
                // Reject scientific notation, negative signs, and other invalid characters
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  setFormData({ ...formData, amount: value });
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                // Sanitize pasted content: remove all non-digit and non-decimal characters
                // Also remove scientific notation, negative signs, etc.
                const sanitized = pastedText.replace(/[^\d.]/g, '');
                // Remove multiple decimal points, keeping only the first one
                const parts = sanitized.split('.');
                const cleaned = parts.length > 0 
                  ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                  : '';
                // Only allow valid number format
                if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                  setFormData({ ...formData, amount: cleaned });
                }
              }}
              fullWidth
              placeholder="0.00"
              helperText={
                formData.fromAccountId
                  ? `In ${accounts.find((acc) => acc.id === formData.fromAccountId && !acc.isArchived)?.currency || ""}`
                  : undefined
              }
              InputProps={{
                endAdornment: setShowFee ? (
                  <InputAdornment position="end">
                    {(() => {
                      const isEditingWithFee = !!(editingTransaction && editingTransaction.type === "transfer" && formData.feeAmount !== undefined && formData.feeAmount !== "");
                      const button = (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (formData.feeAmount === undefined) {
                              // Open fee fields
                              setShowFee(true);
                              setFormData({ ...formData, feeAmount: "" });
                            } else {
                              // Close fee fields
                              setFormData({ ...formData, feeAmount: undefined, feeAccountId: undefined });
                              setShowFee(false);
                            }
                          }}
                          disabled={isEditingWithFee}
                          sx={{
                            minWidth: "auto",
                            px: 1,
                            py: 0.5,
                            color: "text.primary",
                            fontWeight: "normal",
                            textTransform: "none",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                            "&.Mui-disabled": {
                              color: "text.disabled",
                            },
                          }}
                        >
                          {formData.feeAmount === undefined ? "Fee" : "Remove Fee"}
                        </Button>
                      );
                      
                      return isEditingWithFee ? (
                        <Tooltip title="Can't delete fee. Change fee value to 0 instead.">
                          <span>{button}</span>
                        </Tooltip>
                      ) : button;
                    })()}
                  </InputAdornment>
                ) : undefined,
                sx: {
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                },
              }}
            />

            {/* Fee Fields - Show when feeAmount is set */}
            {formData.feeAmount !== undefined && (
              <Stack direction="row" spacing={2}>
                <TextField
                  required
                  label="Fee Amount"
                  type="number"
                  inputProps={{ step: "any", min: "0" }}
                  value={formData.feeAmount || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow digits and a single decimal point
                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                      setFormData({ ...formData, feeAmount: value });
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text');
                    const sanitized = pastedText.replace(/[^\d.]/g, '');
                    const parts = sanitized.split('.');
                    const cleaned = parts.length > 0 
                      ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                      : '';
                    if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                      setFormData({ ...formData, feeAmount: cleaned });
                    }
                  }}
                  sx={{ flex: 1 }}
                  placeholder="0.00"
                  InputProps={{
                    endAdornment: formData.feeAccountId ? (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary">
                          {accounts.find((acc) => acc.id === formData.feeAccountId && !acc.isArchived)?.currency || ""}
                        </Typography>
                      </InputAdornment>
                    ) : undefined,
                    sx: {
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    },
                  }}
                />
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={accounts
                    .filter((acc) => !acc.isArchived)
                    .sort((a, b) => {
                      if (a.group !== b.group) {
                        return a.group.localeCompare(b.group);
                      }
                      return a.accountName.localeCompare(b.accountName);
                    })}
                  value={formData.feeAccountId ? accounts.find((acc) => acc.id === formData.feeAccountId && !acc.isArchived) || null : null}
                  onChange={(_, newValue: Account | null) => {
                    setFormData({
                      ...formData,
                      feeAccountId: newValue?.id || "",
                    });
                  }}
                  groupBy={(option: Account) => option.group}
                  getOptionLabel={(option: Account) =>
                    `${option.accountName} (${formatCurrency(option.amount || 0, option.currency)} ${option.currency})`
                  }
                  filterOptions={(options: Account[], { inputValue }) => {
                    const searchValue = inputValue.toLowerCase();
                    return options.filter(
                      (option) =>
                        option.accountName.toLowerCase().includes(searchValue) ||
                        option.group.toLowerCase().includes(searchValue)
                    );
                  }}
                  renderOption={(props, option: Account) => (
                    <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography variant="body2">{option.accountName}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {formatCurrency(option.amount || 0, option.currency)} ({option.currency})
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <Box
                        sx={{
                          position: "sticky",
                          top: 0,
                          bgcolor: "background.paper",
                          zIndex: 1,
                          py: 0.5,
                          px: 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          {params.group}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ p: 0 }}>
                        {params.children}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fee Account"
                      required
                      helperText={undefined}
                    />
                  )}
                  isOptionEqualToValue={(option: Account, value: Account) => option.id === value.id}
                />
              </Stack>
            )}

            {/* From Account */}
            <Autocomplete
              fullWidth
              options={accounts
                .filter((acc) => !acc.isArchived)
                .sort((a, b) => {
                  if (a.group !== b.group) {
                    return a.group.localeCompare(b.group);
                  }
                  return a.accountName.localeCompare(b.accountName);
                })}
              value={fromAccount || null}
              onChange={(_, newValue: Account | null) => {
                setFormData({
                  ...formData,
                  fromAccountId: newValue?.id || "",
                  toAccountId: newValue?.id === formData.toAccountId ? "" : formData.toAccountId,
                });
              }}
              groupBy={(option: Account) => option.group}
              getOptionLabel={(option: Account) =>
                `${option.accountName} (${formatCurrency(option.amount || 0, option.currency)} ${option.currency})`
              }
              filterOptions={(options: Account[], { inputValue }) => {
                const searchValue = inputValue.toLowerCase();
                return options.filter(
                  (option) =>
                    option.accountName.toLowerCase().includes(searchValue) ||
                    option.group.toLowerCase().includes(searchValue)
                );
              }}
              renderOption={(props, option: Account) => (
                <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2">{option.accountName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      {formatCurrency(option.amount || 0, option.currency)} ({option.currency})
                    </Typography>
                  </Box>
                </Box>
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      bgcolor: "background.paper",
                      zIndex: 1,
                      py: 0.5,
                      px: 1,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                      {params.group}
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ p: 0 }}>
                    {params.children}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="From Account"
                  required
                  helperText={
                    showBalanceHelpers && fromBalance
                      ? (() => {
                          const balanceText = `Balance after transfer: ${formatCurrency(fromBalance.balance, fromAccount?.currency || defaultCurrency)}`;
                          const feeText = fromBalance.feeAmount 
                            ? ` (includes ${formatCurrency(fromBalance.feeAmount, fromAccount?.currency || defaultCurrency)} fee)`
                            : "";
                          const negativeText = fromBalance.isNegative ? " (negative)" : "";
                          return balanceText + feeText + negativeText;
                        })()
                      : undefined
                  }
                  FormHelperTextProps={{
                    sx: {
                      color: showBalanceHelpers && fromBalance && fromBalance.isNegative ? "warning.main" : "text.secondary",
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option: Account, value: Account) => option.id === value.id}
            />

            {/* To Account */}
            <Autocomplete
              fullWidth
              options={accounts
                .filter((acc) => acc.id !== formData.fromAccountId && !acc.isArchived)
                .sort((a, b) => {
                  if (a.group !== b.group) {
                    return a.group.localeCompare(b.group);
                  }
                  return a.accountName.localeCompare(b.accountName);
                })}
              value={toAccount || null}
              onChange={(_, newValue: Account | null) => {
                setFormData({
                  ...formData,
                  toAccountId: newValue?.id || "",
                });
              }}
              groupBy={(option: Account) => option.group}
              getOptionLabel={(option: Account) =>
                `${option.accountName} (${formatCurrency(option.amount || 0, option.currency)} ${option.currency})`
              }
              filterOptions={(options: Account[], { inputValue }) => {
                const searchValue = inputValue.toLowerCase();
                return options.filter(
                  (option) =>
                    option.accountName.toLowerCase().includes(searchValue) ||
                    option.group.toLowerCase().includes(searchValue)
                );
              }}
              renderOption={(props, option: Account) => (
                <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2">{option.accountName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      {formatCurrency(option.amount || 0, option.currency)} ({option.currency})
                    </Typography>
                  </Box>
                </Box>
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      bgcolor: "background.paper",
                      zIndex: 1,
                      py: 0.5,
                      px: 1,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                      {params.group}
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ p: 0 }}>
                    {params.children}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="To Account"
                  required
                  helperText={
                    showBalanceHelpers && toBalance
                      ? (() => {
                          const balanceText = `Balance after transfer: ${formatCurrency(toBalance.balance, toAccount?.currency || defaultCurrency)}`;
                          const feeText = toBalance.feeAmount 
                            ? ` (includes ${formatCurrency(toBalance.feeAmount, toAccount?.currency || defaultCurrency)} fee)`
                            : "";
                          const negativeText = toBalance.isNegative ? " (negative)" : "";
                          return balanceText + feeText + negativeText;
                        })()
                      : undefined
                  }
                  FormHelperTextProps={{
                    sx: {
                      color: showBalanceHelpers && toBalance && toBalance.isNegative ? "warning.main" : "text.secondary",
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option: Account, value: Account) => option.id === value.id}
            />

            {/* Exchange Rate - Show only when currencies differ */}
            {currenciesDiffer && (
              <TextField
                required
                fullWidth
                label="Exchange Rate"
                type="number"
                inputProps={{ step: "0.000001", min: "0.000001" }}
                value={formData.transferRate}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow digits and decimal point, reject scientific notation and negative signs
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setFormData({ ...formData, transferRate: value });
                    if (setRateManuallyEdited) {
                      setRateManuallyEdited(true);
                    }
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  // Sanitize pasted content: remove all non-digit and non-decimal characters
                  const sanitized = pastedText.replace(/[^\d.]/g, '');
                  // Remove multiple decimal points, keeping only the first one
                  const parts = sanitized.split('.');
                  const cleaned = parts.length > 0 
                    ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                    : '';
                  // Only allow valid number format
                  if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                    setFormData({ ...formData, transferRate: cleaned });
                    if (setRateManuallyEdited) {
                      setRateManuallyEdited(true);
                    }
                  }
                }}
                placeholder="Auto-filled from currency settings"
                helperText={(() => {
                  if (!fromAccount || !toAccount) return "";
                  
                  // Calculate default currency rate for from currency
                  const fromDefaultRate = fromAccount.currency !== defaultCurrency 
                    ? calculateRate(defaultCurrency, fromAccount.currency)
                    : 1;
                  const fromDefaultRateDisplay = fromAccount.currency !== defaultCurrency 
                    ? formatRate(fromDefaultRate)
                    : "1";
                  
                  // Calculate converted amount
                  const toAccountDecimalPlaces = getCurrencyDecimalPlaces(toAccount.currency, currencies, defaultCurrency);
                  const convertedAmountDisplay = convertedAmount 
                    ? formatCurrency(convertedAmount, toAccount.currency, toAccountDecimalPlaces)
                    : "";
                  
                  // Build helper text: "1 USD = 375.9398 AMD | = AMD 3,759.4"
                  const defaultRateText = `1 ${defaultCurrency} = ${fromDefaultRateDisplay} ${fromAccount.currency}`;
                  const convertedAmountText = convertedAmountDisplay ? ` | = ${convertedAmountDisplay}` : "";
                  return defaultRateText + convertedAmountText;
                })()}
                InputProps={{
                  endAdornment:
                    showRefreshRate && onRefreshRate ? (
                      <RefreshIconButton
                        onClick={handleRefreshRate}
                        disabled={shouldDisableRefresh}
                        disabledTooltip={
                          rateMatchesSetting || !rateManuallyEdited
                            ? "Rate matches the setting rate."
                            : "Rate has been manually edited. Clear the field to enable refresh."
                        }
                        tooltip="Refresh rate from currency settings"
                      />
                    ) : undefined,
                  sx: {
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  },
                }}
              />
            )}

            {/* Live Calculation Display */}
            {convertedAmount !== null && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "action.hover",
                  color: "text.primary",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" gutterBottom fontWeight="medium" color="text.primary">
                  Transfer Calculation:
                </Typography>
                <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                  <Typography variant="body1" color="text.primary">
                    {formatCurrency(
                      parseFloat(formData.amount),
                      fromAccount?.currency || "USD"
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    →
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="text.primary">
                    {formatCurrency(convertedAmount || 0, toAccount?.currency || "USD")}
                  </Typography>
                </Stack>
                {formData.transferRate && (
                  <Typography variant="caption" sx={{ mt: 0.5, display: "block" }} color="text.secondary">
                    Rate: {formatRate(formData.transferRate)}
                  </Typography>
                )}
              </Paper>
            )}

            {/* Note */}
            <TextField
              label="Note (Optional)"
              value={formData.note}
              onChange={(e) => {
                setFormData({ ...formData, note: e.target.value });
              }}
              fullWidth
              placeholder="Add a note..."
            />

            {/* Attachment Section */}
            {onFileChange && (
              <Box>
                <input
                  ref={fileInputRef as React.RefObject<HTMLInputElement>}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={onFileChange}
                  style={{ display: "none" }}
                  id="transfer-attachment-input"
                />
                <TextField
                  fullWidth
                  label="Attachment (Optional)"
                  value={formData.attachmentFile?.name || formData.attachmentName || ""}
                  onClick={() => {
                    if (!uploadingAttachment && fileInputRef?.current) {
                      fileInputRef.current.click();
                    }
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {formData.attachmentFile || formData.attachmentUrl ? (
                          <>
                            {formData.attachmentUrl && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(formData.attachmentUrl!, "_blank");
                                }}
                                color="primary"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            )}
                            {onRemoveAttachment && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveAttachment();
                                }}
                                disabled={uploadingAttachment}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!uploadingAttachment && fileInputRef?.current) {
                                fileInputRef.current.click();
                              }
                            }}
                            disabled={uploadingAttachment}
                            color="primary"
                          >
                            <AttachFileIcon fontSize="small" />
                          </IconButton>
                        )}
                        {uploadingAttachment && <CircularProgress size={16} />}
                      </Box>
                    ),
                  }}
                  helperText={
                    uploadingAttachment
                      ? "Uploading attachment..."
                      : formData.attachmentFile || formData.attachmentUrl
                      ? undefined
                      : "Supported formats: PDF, PNG, JPG (Max 10MB)"
                  }
                  sx={{ cursor: uploadingAttachment ? "default" : "pointer" }}
                />
              </Box>
            )}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button onClick={onClose} variant="outlined" sx={{ flex: 1, py: 1.5 }}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="transfer-form"
          variant="contained"
          sx={{ flex: 1, py: 1.5 }}
          disabled={isSubmitting || !isValid}
        >
          {editingTransaction && editingTransaction.type === "transfer" ? "Update Transfer" : "Transfer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
